import { EventHandler } from '@omni/events';
import { AppointmentActivity } from "./../../classes/activity/appointment.activity.class";
import { ActivityService } from "./../activity/activity.service";
import {
  Resource,
  IMeetingResourceStatus,
  IResource,
  ResourceParticipantStatus,
  OfflineResource
} from "./../../classes/resource/resource.class";
import { Injectable, signal } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { Contact } from "../../classes/contact/contact.class";

import { Events } from '@omni/events';
import { SelectedSuggestionPillDataModel } from './../../models/search-config-data-model';
import { SearchConfigService } from './../search/search-config.service';
import { ResourceProfiler } from '../logging/resource-profiler.service';

export enum ResourceView {
  MENU = "menu",
  PUSH = "push",
  ADDTOEMAIL = "addtoemail",
  DOCUMENTS = "documents",
  MEETING = "meeting"
}

@Injectable({
  providedIn: 'root'
})
export class ResourceService {
  public allResources: Array<Resource> = [];
  public selectedResourceList: Resource[] = [];
  private highlightedResource$ = new BehaviorSubject<Resource>(null);
  highlightedResource = this.highlightedResource$.asObservable();
  public viewMode: ResourceView = ResourceView.MENU;
  public activeResource: Resource;

  private resourceSelectionSource = new BehaviorSubject<Resource[]>(undefined);
  resourceSelectionObserver = this.resourceSelectionSource.asObservable();

  public isSelectMode = signal(false);
  public offlineResources: OfflineResource[] = [];

  /**
   *  To keep track current Resource view
   */
  // public viewMode: ResourceView;

  public getResourceById(resourceid: string, documentid: string): Resource {
    const idx: string = resourceid ? resourceid : documentid;
    return this.allResources.find(
      pres =>
        pres.ioResourceId === idx || pres.ioDocumentId === idx
    );
  }

  get participantsByResource() {
    return this.activityService.selectedActivity instanceof AppointmentActivity
      ? this.activityService.selectedActivity.sharedResources
      : [];
  }

  recentSearches: SelectedSuggestionPillDataModel[] = [];

  constructor(
    private activityService: ActivityService,
    private events: Events,
    private searchConfigService: SearchConfigService,
    private readonly resourceProfiler: ResourceProfiler,
  ) {
    this.subscribeEvent((status: IMeetingResourceStatus) => {
      let found = this.participantsByResource.find(
        ({ contact, resource }) =>
          contact.ID === status.indskr_contactid &&
          ((resource.ioResourceId &&
            resource.ioResourceId === status.indskr_ioresourceid) ||
            (resource.ioDocumentId &&
              resource.ioDocumentId === status.indskr_iodocumentid))
      );
      if (found) {
        found.statuses = found.statuses || [];
        found.statuses.push({
          status: 1 + +status.indskr_resultstatus,
          date: new Date(+status.indskr_resulttime)
        });
      }
      this.events.publish("refresh_resource_status");
    });
    //this.selectedPresView.next({name:"list", code:"ios-list"});
  }

  subscribeEvent(handler: EventHandler) {
    this.events.unsubscribe("meeting:resourcestatus", handler);
    this.events.subscribe("meeting:resourcestatus", handler);
  }

  clearAllResources() {
    this.allResources = [];
  }

  /**
   * Update list of all Resource
   * Tag Resources that are already downloaded
   *
   * @param raw
   */
  public refreshResourceList(raw: IResource[]) {
    this.resourceProfiler.mark('refresh_resource_list');
    if (!Array.isArray(raw)) {
      console.error('Unsupported data format for resources!');
      return;
    }

    raw = raw.filter(item => item.statecode === '0' && item.statuscode === '1');

    // O(M) Set build instead of O(N x M) repeated .some() per resource
    const offlineIds = new Set(this.offlineResources.map(x => x.ioResourceId));

    this.allResources = raw.map(rawJSON => {
      const resource = new Resource(rawJSON);
      if (offlineIds.has(resource.ioResourceId) || offlineIds.has(resource.ioDocumentId)) {
        resource.downloaded = true;
      }
      return resource;
    });

    this.resourceProfiler.measure('refresh_resource_list_mapping', 'refresh_resource_list', {
      resourceCount: this.allResources.length,
      offlineCount: offlineIds.size,
    });

    // Bulk rebuild with Sets — not per-resource .find()/.some()
    this.resourceProfiler.mark('search_index_build');
    this.rebuildSearchIndex(this.allResources);
    this.resourceProfiler.measure('search_index_build', 'search_index_build', { resourceCount: this.allResources.length });
  }

  public reset() {
    this.highlightedResource$.next(null);
  }

  public highlightResource(resource: Resource) {
    this.resourceProfiler.mark('click_to_render');
    this.resourceProfiler.resetCounters();
    this.highlightedResource$.next(resource);
  }

  addParticipantsForResource(resource: Resource, contacts: Contact[]) {
    contacts.forEach(c => {
      let existing = this.participantsByResource.find(
        obj => obj.resource === resource && obj.contact === c
      );
      if (!existing) {
        existing = { resource: resource, contact: c, statuses: [] };
        this.participantsByResource.push(existing);
      }
      existing.statuses.push({
        status: <number>ResourceParticipantStatus.SENT,
        date: new Date()
      });
    });
  }

  public updateSelResourceList(resource: Resource) {
    let element = this.selectedResourceList.find(r => r.assetID === resource.assetID);
    if (!element) {
        // pres.isSelected = true;
        this.selectedResourceList.push(resource);
    } else {
      // pres.isSelected = false;
      let index = this.selectedResourceList.indexOf(resource);
      this.selectedResourceList.splice(index, 1);
    }
  }

  public setEmailResource(input) {
    this.resourceSelectionSource.next(input);
  }

  /**
   * Bulk rebuild search index using Sets for O(1) dedup.
   * Used during full sync — single pass over all resources.
   */
  public rebuildSearchIndex(resources: Resource[]) {
    if (!this.searchConfigService.isConfigInitiated) {
      this.searchConfigService.initSearchConfigs();
      this.searchConfigService.isConfigInitiated = true;
    }

    const configs = this.searchConfigService.resourcesSearchIndexesConfig;
    const productsConfig = configs.find(c => c.categoryName === 'Products');
    const typesConfig = configs.find(c => c.categoryName === 'Types');
    const taConfig = configs.find(c => c.categoryName === 'Therapeutic Areas');
    const specConfig = configs.find(c => c.categoryName === 'Specialties');
    const dsConfig = configs.find(c => c.categoryName === 'Disease State');

    const products = new Set<string>();
    const types = new Set<string>();
    const tas = new Set<string>();
    const specs = new Set<string>();
    const diseases = new Set<string>();

    for (const resource of resources) {
      for (const brand of resource.brands) {
        if (brand) products.add(brand);
      }
      if (resource.assetType) types.add(resource.assetType);
      for (const ta of resource.therapeuticAreas) {
        if (ta.therapeuticAreaName) tas.add(ta.therapeuticAreaName);
      }
      for (const specialty of resource.specialties) {
        if (specialty.name) specs.add(specialty.name);
      }
      for (const ds of resource.diseaseStates) {
        if (ds?.diseaseStateName) diseases.add(ds.diseaseStateName);
      }
    }

    productsConfig.values = [...products];
    typesConfig.values = [...types];
    taConfig.values = [...tas];
    specConfig.values = [...specs];
    dsConfig.values = [...diseases];
  }

  /**
   * Incremental search index update for delta sync (1-10 resources).
   * Caches config refs per call — no instance-level cache to avoid staleness.
   */
  public mapResourceFieldsToSearchIndex(resource: Resource) {
    if (!this.searchConfigService.isConfigInitiated) {
      this.searchConfigService.initSearchConfigs();
      this.searchConfigService.isConfigInitiated = true;
    }

    const configs = this.searchConfigService.resourcesSearchIndexesConfig;
    const productsConfig = configs.find(c => c.categoryName === 'Products');
    const typesConfig = configs.find(c => c.categoryName === 'Types');
    const taConfig = configs.find(c => c.categoryName === 'Therapeutic Areas');
    const specConfig = configs.find(c => c.categoryName === 'Specialties');
    const dsConfig = configs.find(c => c.categoryName === 'Disease State');

    for (const brand of resource.brands) {
      if (brand && !productsConfig.values.includes(brand)) {
        productsConfig.values.push(brand);
      }
    }
    if (resource.assetType && !typesConfig.values.includes(resource.assetType)) {
      typesConfig.values.push(resource.assetType);
    }
    for (const ta of resource.therapeuticAreas) {
      if (ta.therapeuticAreaName && !taConfig.values.includes(ta.therapeuticAreaName)) {
        taConfig.values.push(ta.therapeuticAreaName);
      }
    }
    for (const specialty of resource.specialties) {
      if (specialty.name && !specConfig.values.includes(specialty.name)) {
        specConfig.values.push(specialty.name);
      }
    }
    for (const ds of resource.diseaseStates) {
      if (ds?.diseaseStateName && !dsConfig.values.includes(ds.diseaseStateName)) {
        dsConfig.values.push(ds.diseaseStateName);
      }
    }
  }

  public getOfflineResourceById(resourceid: string): OfflineResource {
    return this.offlineResources.find(r => r.ioResourceId === resourceid);
  }

  public refreshOfflineResourceList(raw: any) {
    if (Array.isArray(raw)) {
      this.offlineResources = raw;
    } else {
      console.error('Unsupported data format for offline resources!');
    }
  }

}
